import { useEffect, useRef, useState } from 'react';

const KEYWORDS = [
  {
    label: 'Data Driven',
    ko: '데이터 중심 전략',
    desc: '수치가 전략의 시작입니다.\n감이 아닌 데이터로 결정합니다.',
    icon: 'ri-bar-chart-2-line',
  },
  {
    label: 'Performance Focus',
    ko: '성과 집착주의',
    desc: 'ROAS, 전환율, 그리고 매출.\n오직 성과 지표만을 추구합니다.',
    icon: 'ri-focus-3-line',
  },
  {
    label: 'Real Execution',
    ko: '실행력이 전부',
    desc: '계획보다 실행이 먼저입니다.\n결과로만 모든 것을 말합니다.',
    icon: 'ri-flashlight-line',
  },
  {
    label: 'Scalable Growth',
    ko: '확장 가능한 성장',
    desc: '성장이 멈추지 않도록.\n지속 가능한 구조를 설계합니다.',
    icon: 'ri-rocket-2-line',
  },
];

function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setVisible(true); obs.disconnect(); }
    }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, visible };
}

export default function IANMediaSection() {
  const hero = useInView(0.1);
  const keywords = useInView(0.2);
  const cta = useInView(0.2);

  return (
    <section
      id="ian-media"
      className="relative bg-[#020409] overflow-hidden"
      style={{ fontFamily: "'Space Grotesk', 'Noto Sans KR', sans-serif" }}
    >
      {/* ── Background texture ── */}
      <div className="absolute inset-0 pointer-events-none select-none">
        {/* Radial glow — top left */}
        <div
          className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full opacity-[0.06]"
          style={{ background: 'radial-gradient(circle, #00FF87 0%, transparent 70%)' }}
        />
        {/* Radial glow — bottom right */}
        <div
          className="absolute -bottom-40 -right-20 w-[500px] h-[500px] rounded-full opacity-[0.05]"
          style={{ background: 'radial-gradient(circle, #00FF87 0%, transparent 70%)' }}
        />
        {/* Fine grid */}
        <div
          className="absolute inset-0 opacity-[0.025]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,.12) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.12) 1px, transparent 1px)',
            backgroundSize: '64px 64px',
          }}
        />
      </div>

      {/* ═══════════════════════════════════ HERO COPY ═══════════════════════════════════ */}
      <div
        ref={hero.ref}
        className="relative max-w-6xl mx-auto px-5 md:px-12 pt-14 md:pt-32 pb-12 md:pb-24"
      >
        {/* Eyebrow */}
        <div
          className="flex items-center gap-2.5 mb-6 md:mb-12"
          style={{
            opacity: hero.visible ? 1 : 0,
            transform: hero.visible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.7s ease, transform 0.7s ease',
          }}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-[#00FF87] animate-pulse" />
          <span className="text-[#00FF87]/60 text-xs font-bold tracking-[0.25em] uppercase">
            이안미디어 소개
          </span>
          <span className="flex-1 h-px bg-gradient-to-r from-[#00FF87]/20 to-transparent" />
        </div>

        {/* Main title */}
        <h2
          className="font-black text-white leading-[1.08] mb-6 md:mb-10"
          style={{
            fontSize: 'clamp(2rem, 6vw, 5rem)',
            letterSpacing: '-0.02em',
            opacity: hero.visible ? 1 : 0,
            transform: hero.visible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'opacity 0.7s ease 0.1s, transform 0.7s ease 0.1s',
          }}
        >
          결과를 만듭니다,<br />
          <span
            className="relative inline-block"
            style={{ color: '#00FF87', textShadow: '0 0 80px rgba(0,255,135,0.4)' }}
          >
            광고만 하지 않습니다
            {/* Underline */}
            <span
              className="absolute left-0 -bottom-1 h-[3px] rounded-full"
              style={{
                background: 'linear-gradient(90deg, #00FF87 0%, transparent 100%)',
                width: hero.visible ? '100%' : '0%',
                transition: 'width 1s ease 0.6s',
              }}
            />
          </span>
        </h2>

        {/* Divider */}
        <div
          className="h-px mb-8 md:mb-14"
          style={{
            background: 'linear-gradient(90deg, #00FF87/20 0%, transparent 100%)',
            opacity: hero.visible ? 1 : 0,
            transition: 'opacity 0.8s ease 0.3s',
          }}
        />

        {/* Two-column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-16 items-start">

          {/* LEFT — big manifesto quote */}
          <div
            style={{
              opacity: hero.visible ? 1 : 0,
              transform: hero.visible ? 'translateY(0)' : 'translateY(24px)',
              transition: 'opacity 0.8s ease 0.2s, transform 0.8s ease 0.2s',
            }}
          >
            <blockquote
              className="font-black text-white leading-[1.12] mb-5 md:mb-8"
              style={{
                fontSize: 'clamp(1.5rem, 4.5vw, 3.4rem)',
                letterSpacing: '-0.015em',
              }}
            >
              <span className="text-white/25">단순히 광고를<br />집행하지 않습니다.</span>
              <br />
              <span style={{ color: '#00FF87', textShadow: '0 0 60px rgba(0,255,135,0.35)' }}>
                진짜 성과를<br />만들어냅니다.
              </span>
            </blockquote>

            {/* Signature line */}
            <div className="flex items-center gap-3">
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 border border-[#00FF87]/30"
                style={{ background: 'rgba(0,255,135,0.08)' }}
              >
                <i className="ri-building-line text-[#00FF87] text-xs" />
              </div>
              <span className="text-white/30 text-sm font-medium tracking-wide">IAN MEDIA</span>
              <span className="h-px flex-1 bg-white/5" />
            </div>
          </div>

          {/* RIGHT — description */}
          <div
            className="space-y-4 md:space-y-6"
            style={{
              opacity: hero.visible ? 1 : 0,
              transform: hero.visible ? 'translateY(0)' : 'translateY(24px)',
              transition: 'opacity 0.8s ease 0.35s, transform 0.8s ease 0.35s',
            }}
          >
            <p
              className="text-white/60 leading-[1.85] text-sm md:text-base"
            >
              이안미디어는 ROAS 극대화와 실질적인 비즈니스 성장에 집중하는<br />
              데이터 기반 퍼포먼스 마케팅 전문 대행사입니다.<br />
              <br />
              축적된 실제 캠페인 데이터와 지속적인 최적화를 통해,<br />
              진짜로 전환되는 전략을 설계합니다.
            </p>

            <div className="h-px bg-white/5" />

            <p
              className="text-white/80 leading-[1.85] font-medium text-sm md:text-base"
            >
              우리는 단순한 대행사가 아닙니다,
              <br className="block" />
              <span style={{ color: '#00FF87', textShadow: '0 0 30px rgba(0,255,135,0.3)' }}>
                우리는 당신의 성장 파트너입니다.
              </span>
            </p>

            {/* Mini stats */}
            <div className="grid grid-cols-3 gap-2 md:gap-3 pt-2">
              {[
                { n: '350+', l: '성공 브랜드' },
                { n: '12x', l: '평균 ROAS' },
                { n: '6Y+', l: '운영 경력' },
              ].map((s, i) => (
                <div
                  key={i}
                  className="rounded-xl p-2.5 md:p-3 border border-white/5 text-center"
                  style={{ background: 'rgba(255,255,255,0.025)' }}
                >
                  <div
                    className="font-black leading-none mb-1"
                    style={{
                      fontSize: 'clamp(1rem, 2vw, 1.5rem)',
                      color: '#00FF87',
                      fontFamily: "'Space Grotesk', sans-serif",
                    }}
                  >
                    {s.n}
                  </div>
                  <div className="text-white/30 text-xs">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════ KEYWORD PILLS ═══════════════════════════════════ */}
      <div ref={keywords.ref} className="relative border-t border-white/5">
        <div className="max-w-6xl mx-auto px-5 md:px-12 py-10 md:py-20">

          {/* Section label */}
          <div
            className="flex items-center gap-3 mb-8 md:mb-12"
            style={{
              opacity: keywords.visible ? 1 : 0,
              transition: 'opacity 0.6s ease',
            }}
          >
            <span className="w-[2px] h-6 bg-[#00FF87]/60 rounded-full" />
            <p className="text-white/70 text-xs md:text-base font-extrabold tracking-[0.32em] uppercase" style={{ letterSpacing: '0.32em' }}>
              핵심 아이덴티티
            </p>
            <span className="flex-1 h-px bg-gradient-to-r from-[#00FF87]/15 to-transparent" />
          </div>

          {/* Cards grid — 숫자로 증명합니다 스타일: 단일 외곽 테두리 + 내부 구분선 */}
          <div
            className="border border-white/20 rounded-2xl overflow-hidden"
            style={{
              opacity: keywords.visible ? 1 : 0,
              transform: keywords.visible ? 'translateY(0)' : 'translateY(16px)',
              transition: 'opacity 0.6s ease 0.1s, transform 0.6s ease 0.1s',
            }}
          >
            <div className="grid grid-cols-2 md:grid-cols-4">
              {KEYWORDS.map((k, i) => {
                // 반응형 내부 구분선 처리
                // 모바일(2열): 0→border-r+border-b, 1→border-b, 2→border-r, 3→없음
                // 데스크탑(4열): 0,1,2→border-r, 3→없음
                const borderClass =
                  i === 0 ? 'border-r border-b md:border-b-0 border-white/20' :
                  i === 1 ? 'border-b md:border-b-0 md:border-r border-white/20' :
                  i === 2 ? 'border-r border-white/20' : '';

                return (
                  <div
                    key={i}
                    className={`group relative p-5 md:p-8 pb-6 md:pb-10 cursor-default transition-all duration-300 ${borderClass}`}
                    style={{
                      background: 'rgba(10, 13, 30, 1)',
                      opacity: keywords.visible ? 1 : 0,
                      transform: keywords.visible ? 'translateY(0)' : 'translateY(20px)',
                      transition: `opacity 0.55s ease ${0.1 * i + 0.1}s, transform 0.55s ease ${0.1 * i + 0.1}s, background 0.3s ease`,
                    }}
                    onMouseEnter={e => {
                      (e.currentTarget as HTMLElement).style.background = 'rgba(0,255,135,0.05)';
                    }}
                    onMouseLeave={e => {
                      (e.currentTarget as HTMLElement).style.background = 'rgba(10,13,30,1)';
                    }}
                  >
                    {/* Hover glow */}
                    <div
                      className="absolute -top-8 -right-8 w-24 h-24 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                      style={{ background: 'radial-gradient(circle, rgba(0,255,135,0.12) 0%, transparent 70%)' }}
                    />

                    {/* Icon */}
                    <div
                      className="w-10 h-10 md:w-14 md:h-14 rounded-xl flex items-center justify-center mb-4 md:mb-5 border border-white/10 group-hover:border-[#00FF87]/30 transition-all duration-300"
                      style={{ background: 'rgba(255,255,255,0.04)' }}
                    >
                      <i className={`${k.icon} text-lg md:text-2xl text-white/30 group-hover:text-[#00FF87] transition-colors duration-300`} />
                    </div>

                    {/* Labels */}
                    <p
                      className="font-black text-white/70 group-hover:text-white transition-colors duration-300 mb-1 leading-tight text-sm md:text-xl"
                      style={{ letterSpacing: '-0.01em' }}
                    >
                      {k.label}
                    </p>
                    <p className="text-[#00FF87]/50 group-hover:text-[#00FF87]/80 text-xs md:text-sm font-semibold tracking-wide transition-colors duration-300 mb-2.5 md:mb-4">
                      {k.ko}
                    </p>
                    <p className="text-white/25 group-hover:text-white/45 text-xs md:text-sm leading-[1.9] transition-colors duration-300 break-words">
                      {k.desc.split('\n').map((line, li) => (
                        <span key={li}>
                          {li > 0 && <br className="hidden md:block" />}
                          {line}
                        </span>
                      ))}
                    </p>

                    {/* Bottom accent line on hover */}
                    <div
                      className="absolute bottom-0 left-0 h-[2px] transition-all duration-500"
                      style={{
                        background: 'linear-gradient(90deg, #00FF87, transparent)',
                        width: '0%',
                      }}
                      ref={el => {
                        if (el) {
                          el.closest('.group')?.addEventListener('mouseenter', () => { el.style.width = '60%'; });
                          el.closest('.group')?.addEventListener('mouseleave', () => { el.style.width = '0%'; });
                        }
                      }}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════ CTA STRIP ═══════════════════════════════════ */}
      <div
        ref={cta.ref}
        className="relative border-t border-white/5 overflow-hidden"
      >
        {/* Centered radial glow */}
        <div className="absolute inset-0 pointer-events-none select-none">
          <div
            className="absolute left-1/2 -translate-x-1/2 -top-20 w-[700px] h-[400px]"
            style={{ background: 'radial-gradient(ellipse, rgba(0,255,135,0.07) 0%, transparent 65%)' }}
          />
          {/* Subtle top line accent */}
          <div
            className="absolute top-0 left-1/4 right-1/4 h-px"
            style={{ background: 'linear-gradient(90deg, transparent, rgba(0,255,135,0.35), transparent)' }}
          />
        </div>

        <div
          className="relative max-w-5xl mx-auto px-5 md:px-12 py-14 md:py-24 text-center"
          style={{
            opacity: cta.visible ? 1 : 0,
            transform: cta.visible ? 'translateY(0)' : 'translateY(24px)',
            transition: 'opacity 0.8s ease 0.1s, transform 0.8s ease 0.1s',
          }}
        >
          {/* Eyebrow */}
          <div className="flex items-center justify-center gap-2.5 mb-5 md:mb-8">
            <span className="w-1.5 h-1.5 rounded-full bg-[#00FF87] animate-pulse" />
            <p className="text-[#00FF87]/55 text-xs font-bold tracking-[0.25em] uppercase">
              성장할 준비가 되셨나요?
            </p>
            <span className="w-1.5 h-1.5 rounded-full bg-[#00FF87] animate-pulse" />
          </div>

          {/* Main headline */}
          <h2
            className="font-black text-white leading-[1.45] md:leading-[1.25] mb-6 md:mb-10"
            style={{
              fontSize: 'clamp(1.1rem, 4.5vw, 2.8rem)',
              letterSpacing: '-0.02em',
            }}
          >
            <span className="md:whitespace-nowrap">이제 결과를 바꿀 준비가 되셨다면,</span>
            <br className="md:hidden" />
            {' '}
            <span className="md:whitespace-nowrap" style={{ color: '#00FF87', textShadow: '0 0 70px rgba(0,255,135,0.45)' }}>
              이안미디어와 함께 시작하세요.
            </span>
          </h2>

          {/* Trust pills */}
          <div className="flex flex-wrap items-center justify-center gap-2 md:gap-3 mb-8 md:mb-10">
            {[
              { icon: 'ri-money-dollar-circle-line', text: '상담 비용 0원' },
              { icon: 'ri-shield-check-line', text: '계약 강요 없음' },
              { icon: 'ri-time-line', text: '24시간 내 응답' },
            ].map((item, i) => (
              <span
                key={i}
                className="flex items-center gap-1.5 text-white/40 text-xs border border-white/10 rounded-full px-3.5 py-1.5 bg-white/[0.03]"
              >
                <i className={`${item.icon} text-[#00FF87]/60 text-xs flex-shrink-0`}></i>
                {item.text}
              </span>
            ))}
          </div>

          {/* CTA Button */}
          <button
            onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
            className="group relative inline-flex items-center justify-center gap-3 font-black text-black px-10 md:px-16 py-4 md:py-5 rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap overflow-hidden text-sm md:text-lg w-full sm:w-auto"
            style={{
              background: '#00FF87',
              boxShadow: '0 0 60px rgba(0,255,135,0.32), 0 0 120px rgba(0,255,135,0.1)',
            }}
          >
            <span
              className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
              style={{ background: 'linear-gradient(105deg, transparent 35%, rgba(255,255,255,0.25) 50%, transparent 65%)' }}
            />
            <span className="relative z-10">무료 진단 신청하기</span>
            <div className="relative z-10 w-5 h-5 flex items-center justify-center">
              <i className="ri-arrow-right-line text-lg group-hover:translate-x-1 transition-transform duration-200" />
            </div>
          </button>

          {/* Sub note */}
          <p className="text-white/18 text-xs mt-4 leading-relaxed">
            15초면 신청 완료 · 부담 없는 첫 상담
          </p>
        </div>
      </div>
    </section>
  );
}
